// 风控阈值设置页面
import React, { ReactNode, useEffect, useState } from 'react';
import _ from 'lodash';
import { Box, Grid, InputAdornment, Typography, Skeleton, Button } from '@mui/material';
import { observer } from 'mobx-react-lite';
import CardView from '../../component/card/card-view';
import CommonInput from '../../component/form/common-input.component';
import i18n from '../../i18n';
import apiManage from '../../request';
import { COLOR_KEY, getModeColor } from '../../themes/customization.value';
import { GetThemeMode } from '../../themes/tool';
import GlobalNotification from '../../component/common/snackbar';
import LoadingOverlayButton from '../../component/button/loading-overlay-button.component';
import {
    LocalRiskSyncStatus,
    RestaurantInfoUpdateResponse,
    RiskThresholdRequest,
} from '../../request/api/risk';
import BASE_CHECK_TYPE from '../../constants/regular.constant';
import { MobxObjectsManager } from '../mobx-objs/mobx-manager';
import MainPageState from '../main/main-page-states';
import './scss/risk-threshold-settings.scss'
import { AntSwitch } from '../../component/switch/IOSSwitch';
import {
    defaultFormValue,
    isWhatsAppReceiverRequired,
} from './risk-threshold-settings-defaults';

const mainPageData = MobxObjectsManager.get(MainPageState);

/** 最大优惠：金额/百分比是否视为已填写（用于推导 mode） */
function hasDiscountNumber(value: unknown): boolean {
    if (value === undefined || value === null || value === '') {
        return false;
    }
    if (typeof value === 'number' && !Number.isNaN(value)) {
        return true;
    }
    return false;
}

/**
 * 判断餐厅信息保存响应是否包含风控阈值的本地同步结果。
 * @param response - 餐厅信息保存接口成功响应
 * @returns 是否为带本地同步状态的风控响应
 */
function hasLocalSyncStatus(
    response: RestaurantInfoUpdateResponse,
): response is Extract<RestaurantInfoUpdateResponse, { local_sync_status: LocalRiskSyncStatus }> {
    return 'local_sync_status' in response;
}

/**
 * 根据本地同步状态返回对应的提示文案 key。
 * @param status - 本地同步状态
 * @returns 本地同步提示文案 key
 */
function getLocalSyncMessageKey(status: LocalRiskSyncStatus): string {
    switch (status) {
        case 'SYNCED':
            return 'restaurant_risk_page_save_local_sync_success';
        case 'FAILED':
            return 'restaurant_risk_page_save_local_sync_failed';
        case 'SKIPPED_TUNNEL_UNAVAILABLE':
            return 'restaurant_risk_page_save_local_sync_tunnel_unavailable';
        case 'SKIPPED_INCOMPATIBLE_VERSION':
            return 'restaurant_risk_page_save_local_sync_incompatible_version';
        case 'SKIPPED_NO_DATABASE_CONFIG':
            return 'restaurant_risk_page_save_local_sync_no_database_config';
        case 'SKIPPED':
        default:
            return 'restaurant_risk_page_save_local_sync_skipped';
    }
}

/**
 * 根据两个输入框推导后端的 mode / trigger_logic（与 App 一致：仅金额→AMOUNT，仅百分比→PERCENT，双填→BOTH + OR）
 * @param amount - 金额输入
 * @param percent - 百分比输入
 */
function deriveMaxDiscountModeAndTrigger(
    amount: unknown,
    percent: unknown,
): { mode: 'AMOUNT' | 'PERCENT' | 'BOTH'; trigger: 'OR' } {
    const hasA = hasDiscountNumber(amount);
    const hasP = hasDiscountNumber(percent);
    if (hasA && hasP) {
        return { mode: 'BOTH', trigger: 'OR' };
    }
    if (hasA) {
        return { mode: 'AMOUNT', trigger: 'OR' };
    }
    if (hasP) {
        return { mode: 'PERCENT', trigger: 'OR' };
    }
    return { mode: 'AMOUNT', trigger: 'OR' };
}

/**
 * 同步单块「最大优惠」的 mode、trigger，并清除与当前 mode 互斥的另一字段（仅金额时不保留百分比，反之亦然）
 * @param prev - 当前表单
 * @param side - 堂食或外卖
 */
function applyMaxDiscountSideSync(
    prev: RiskThresholdRequest,
    side: 'dine_in' | 'delivery',
): RiskThresholdRequest {
    const next: RiskThresholdRequest = { ...prev };
    if (side === 'dine_in') {
        const { mode, trigger } = deriveMaxDiscountModeAndTrigger(
            next.dine_in_max_discount_amount,
            next.dine_in_max_discount_percent,
        );
        next.dine_in_max_discount_mode = mode;
        next.dine_in_max_discount_trigger_logic = trigger;
        if (mode === 'AMOUNT') {
            next.dine_in_max_discount_percent = undefined;
        } else if (mode === 'PERCENT') {
            next.dine_in_max_discount_amount = undefined;
        }
    } else {
        const { mode, trigger } = deriveMaxDiscountModeAndTrigger(
            next.delivery_max_discount_amount,
            next.delivery_max_discount_percent,
        );
        next.delivery_max_discount_mode = mode;
        next.delivery_max_discount_trigger_logic = trigger;
        if (mode === 'AMOUNT') {
            next.delivery_max_discount_percent = undefined;
        } else if (mode === 'PERCENT') {
            next.delivery_max_discount_amount = undefined;
        }
    }
    return next;
}

/**
 * 堂食、外卖两块最大优惠均按当前输入推导并写回 mode / trigger / 互斥字段
 * @param prev - 当前表单快照
 */
function syncAllMaxDiscountBlocks(prev: RiskThresholdRequest): RiskThresholdRequest {
    return applyMaxDiscountSideSync(applyMaxDiscountSideSync(prev, 'dine_in'), 'delivery');
}

/**
 * 接口已带回间隔分钟、但未带回订单数时，默认订单数为 1（「N 单 / 窗口分钟内」）
 * @param fv - 合并餐厅详情后的表单
 */
function migrateDineInOrderCloseWindowNumberDefault(fv: RiskThresholdRequest): RiskThresholdRequest {
    const hasNumber = hasDiscountNumber(fv.dine_in_order_close_check_window_number);
    const hasMinutes = hasDiscountNumber(fv.dine_in_order_close_interval_minutes);
    if (hasNumber || !hasMinutes) {
        return fv;
    }
    return {
        ...fv,
        dine_in_order_close_check_window_number: 1,
    };
}

// 风控阈值设置主组件
const RiskThresholdSettings: React.FC = observer(() => {
    // 当前表单值
    const [formValue, setFormValue] = useState<RiskThresholdRequest>(defaultFormValue);
    // 页面加载/切换餐厅时的骨架屏加载状态
    const [loading, setLoading] = useState<boolean>(false);
    // 保存提交中（与公共 LoadingOverlayButton 同步，避免仅 disabled 时无反馈）
    const [saveLoading, setSaveLoading] = useState(false);

    // 当前主题模式，用于取语义色（标签深色、描边、投影等与 Figma 对齐）
    const themeMode = GetThemeMode();
    /** 按当前主题解析 COLOR_KEY，供 sx / 内联样式使用 */
    const getColor = (key: COLOR_KEY) => getModeColor(key, themeMode);

    // 通用字段变更处理（根据 key 更新表单值）
    const handleChange = <K extends keyof RiskThresholdRequest>(key: K, value: RiskThresholdRequest[K]) => {
        setFormValue((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    /**
     * 表单项左侧说明文案（设计稿：标题-加粗，深色字体）
     * @param textKey - i18n key，或已翻译的展示文案（如部分字段传入 i18n.t 结果）
     */
    const renderLeftText = (textKey: string) => (
        <Typography variant="h3" sx={{ color: getColor(COLOR_KEY.textDark) }}>
            {i18n.t(textKey)}
        </Typography>
    );

    // 公共的单行设置布局（左文案 + 右控件，垂直居中对齐）
    const renderSettingRow = (labelKey: string, rightNode: ReactNode) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ flex: 1, pr: 2 }}>{renderLeftText(labelKey)}</Box>
            <Box sx={{ flexShrink: 0 }}>{rightNode}</Box>
        </Box>
    );

    // 保存前表单必填项校验
    const validateForm = () => {
        let isValid = true;
        let firstErrorMsg = '';

        // 只记录第一条错误信息
        const setErrorOnce = (msg: string) => {
            if (!isValid) return;
            isValid = false;
            firstErrorMsg = msg;
        };

        // 数字类字段通用校验（labelKey 为 i18n key，与同页其他必填提示一致）
        const checkNumberField = (labelKey: string, value: unknown) => {
            if (value === undefined || value === null || value === '' || Number.isNaN(value)) {
                setErrorOnce(`${i18n.t('input_rule_text_required')} ${i18n.t(labelKey)}`);
            }
        };

        // 任一 WhatsApp 推送开启时，接收号码不能为空或仅包含空格
        if (
            isWhatsAppReceiverRequired(formValue) &&
            !formValue.risk_notify_whatsapp_receiver?.toString().trim()
        ) {
            setErrorOnce(
                `${i18n.t('input_rule_text_required')} ${i18n.t(
                    'intelligent_risk_control_page_form_risk_notify_whatsapp_enabled',
                )}`,
            );
        }

        // 左右两侧字段统一拉平，按配置做校验
        const fieldConfigs = [...fieldConfigsLeft, ...fieldConfigsRight];

        fieldConfigs.forEach((cfg) => {
            if (!isValid) return;
            // 最大优惠：两个输入框至少填一项（与 App 一致，由填写情况推导 mode）
            if (cfg.type === 'DISCOUNT') {
                const label = cfg.labelKey as string;
                const amountVal = formValue[cfg.amountKey];
                const percentVal = formValue[cfg.percentKey];
                if (!hasDiscountNumber(amountVal) && !hasDiscountNumber(percentVal)) {
                    setErrorOnce(`${i18n.t('input_rule_text_required')} ${i18n.t(label)}`);
                }
            }
            // 数字类：校验值是否为空
            if (cfg.type === 'NUMBER') {
                const label = cfg.labelKey as string;
                const value = formValue[cfg.key];
                checkNumberField(label, value);
            }
            // 堂食/外卖：订单关闭检测双输入（订单数 + 窗口时长；堂食为分钟、外卖为秒）
            if (cfg.type === 'ORDER_CLOSE_CHECK_WINDOW') {
                const labelKey = cfg.labelKey as string;
                checkNumberField(labelKey, formValue[cfg.numberKey]);
                if (!isValid) return;
                checkNumberField(labelKey, formValue[cfg.windowDurationKey]);
            }
            // 邮件通知：仅在开启时校验邮箱和发送模式
            if (cfg.type === 'EMAIL_NOTIFY') {
                if (formValue.risk_notify_email_enabled) {
                    const email = (formValue.risk_notify_email_receiver ?? '').toString();
                    const mode = formValue.risk_notify_email_send_mode;
                    if (!email) {
                        setErrorOnce(`${i18n.t('input_rule_text_required')} ${i18n.t('general_text_email')}`);
                        return;
                    }
                    // 使用全局正则配置校验邮箱格式
                    const emailRule = BASE_CHECK_TYPE.email;
                    if (emailRule && !emailRule.regular.test(email)) {
                        const text = i18n.t(emailRule.helperText, emailRule.i18nRule);
                        setErrorOnce(text);
                        return;
                    }
                    if (!mode) {
                        setErrorOnce(`${i18n.t('input_rule_text_required')} ${i18n.t('intelligent_risk_control_page_form_risk_notify_email_enabled')}`);
                        return;
                    }
                }
            }
        });

        // 若校验不通过，弹出第一条错误提示
        if (!isValid) {
            GlobalNotification.error(firstErrorMsg || i18n.t('dis_setting_page_type_entire_info'));
        }

        return isValid;
    };

    // 点击保存：先校验表单，再调用后端接口
    const handleSave = async () => {
        const stringId = mainPageData.currentRestaurant.string_id;
        // 未选择餐厅，直接提示
        if (!stringId) {
            GlobalNotification.error(i18n.t('menu_store_msg_no_restaurant'));
            return;
        }
        // 校验不通过，阻止提交
        if (!validateForm()) {
            return;
        }
        setSaveLoading(true);
        try {
            // 提交前再次对齐 mode / trigger，避免状态与输入短暂不一致
            const payload = syncAllMaxDiscountBlocks(formValue);
            const res = await apiManage.patch_restaurant_string_id_info(stringId, payload);
            const response = res as RestaurantInfoUpdateResponse & {
                error?: boolean;
                success?: boolean;
            };
            if (response && !response.error && response.success !== false) {
                if (hasLocalSyncStatus(res)) {
                    const messageKey = getLocalSyncMessageKey(res.local_sync_status);
                    if (res.local_sync_status === 'SYNCED') {
                        GlobalNotification.success(
                            i18n.t(messageKey),
                        );
                    } else {
                        GlobalNotification.warning(i18n.t(messageKey));
                    }
                } else {
                    GlobalNotification.success(i18n.t('google_text_public_text_save_successfully'));
                }
            } else {
                GlobalNotification.error(i18n.t('global_save_text_failed'));
            }
        } catch (e) {
            GlobalNotification.error(i18n.t('global_save_text_failed'));
        } finally {
            setSaveLoading(false);
        }
    };

    // 智能推荐阈值
    const handleApplyRecommend = async () => {
        const stringId = mainPageData.currentRestaurant.string_id;
        // 未选择餐厅，直接提示
        if (!stringId) {
            GlobalNotification.error(i18n.t('menu_store_msg_no_restaurant'));
            return;
        }
        setLoading(true);
        try {
            const res = await apiManage.get_restaurant_risk_recommendation(stringId);
            if (res && !(res as any).error) {
                const data = res as any;
                setFormValue((prev) =>
                    syncAllMaxDiscountBlocks({
                        ...prev,
                        ...data,
                        __meta: undefined,
                        delivery_max_discount_enabled: true,
                        dine_in_max_discount_enabled: true,
                    } as RiskThresholdRequest),
                );
                GlobalNotification.success(i18n.t('global_text_operate_success'));
            } else {
                GlobalNotification.error(i18n.t('general_text_operate_failure'));
            }
        } catch (e) {
            GlobalNotification.error(i18n.t('general_text_operate_failure'));
        }
        setLoading(false);
    }

    // 左侧字段配置：堂食相关、取消订单提示等
    const fieldConfigsLeft: FieldConfig[] = [
        // 堂食 最大优惠金额
        {
            type: 'DISCOUNT',
            key: 'dine_in_max_discount_amount',
            labelKey: 'intelligent_risk_control_page_form_max_discount_amount_dine_in',
            modeKey: 'dine_in_max_discount_mode',
            amountKey: 'dine_in_max_discount_amount',
            percentKey: 'dine_in_max_discount_percent',
            triggerLogicKey: 'dine_in_max_discount_trigger_logic'
        },
        // 最大退菜数量
        {
            type: 'NUMBER',
            key: 'dine_in_max_refund_item_count',
            labelKey: 'intelligent_risk_control_page_form_max_refund_count',
        },
        // 最大退菜金额
        {
            type: 'NUMBER',
            key: 'dine_in_max_refund_amount',
            labelKey: i18n.t('intelligent_risk_control_page_form_max_refund_amount'),
            adornment: <InputAdornment position='end'>€</InputAdornment>,
        },
        // 点菜数量异常
        {
            type: 'NUMBER',
            key: 'dine_in_abnormal_item_count_threshold',
            labelKey: 'intelligent_risk_control_page_form_avg_dish_count_dine_in',
        },
        // 用餐时间最大值
        {
            type: 'NUMBER',
            key: 'dine_in_max_dining_duration_minutes',
            labelKey: 'intelligent_risk_control_page_form_dine_in_duration_max',
            adornment: <InputAdornment position='end'>min</InputAdornment>,
        },
        // 订单关闭间隔：订单数 + 窗口时长（分钟，接口 dine_in_order_close_interval_minutes）
        {
            type: 'ORDER_CLOSE_CHECK_WINDOW',
            labelKey: 'intelligent_risk_control_page_form_order_close_interval_minutes',
            numberKey: 'dine_in_order_close_check_window_number',
            windowDurationKey: 'dine_in_order_close_interval_minutes',
            windowDurationUnit: 'minutes',
        },

        // 取消订单风险提示
        {
            type: 'SWITCH',
            key: 'cancel_order_risk_alert_enabled',
            labelKey: 'intelligent_risk_control_page_form_cancel_order_total',
        },
    ];
    // 右侧字段配置：外卖相关、通知相关等
    const fieldConfigsRight: FieldConfig[] = [
        // 外卖 - 最大优惠金额/比例限制
        {
            type: 'DISCOUNT',
            labelKey: 'intelligent_risk_control_page_form_max_discount_amount_delivery',
            modeKey: 'delivery_max_discount_mode',
            amountKey: 'delivery_max_discount_amount',
            percentKey: 'delivery_max_discount_percent',
            key: 'delivery_max_discount_amount',
            triggerLogicKey: 'delivery_max_discount_trigger_logic'
        },
        {
            type: 'ORDER_CLOSE_CHECK_WINDOW',
            labelKey:
                'intelligent_risk_control_page_form_delivery_order_close_check_window_minutes',
            numberKey: 'delivery_order_close_check_window_number',
            windowDurationKey: 'delivery_order_close_check_window_seconds',
            windowDurationUnit: 'seconds',
        },

        // 营业分析总结报告：WhatsApp 开关（设计稿在「WhatsApp 风控推送」区块上方，主标题 + 括号内说明）
        {
            type: 'SWITCH',
            key: 'shift_briefing_whatsapp_enabled',
            labelKey: 'intelligent_risk_control_page_form_shift_briefing_whatsapp_enabled',
            subLabelKey: 'intelligent_risk_control_page_form_shift_briefing_whatsapp_enabled_sub',
        },

        // WhatsApp 风控推送设置
        {
            type: 'WHATSAPP_NOTIFY',
            key: 'risk_notify_whatsapp_enabled',
            labelKey: 'intelligent_risk_control_page_form_risk_notify_whatsapp_enabled',
        },

        // Email 风控推送设置（包含邮箱 + 发送模式）
        {
            type: 'EMAIL_NOTIFY',
            labelKey: 'intelligent_risk_control_page_form_risk_notify_email_enabled',
            key: 'risk_notify_email_enabled',
        },
    ];

    // 根据字段配置渲染对应控件
    const renderFieldByConfig = (cfg: FieldConfig) => {
        switch (cfg.type) {
            // 最大优惠：仅两个输入框，由填写情况推导 mode（与 App 一致）；视图按设计稿为纵向两条圆角条
            case 'DISCOUNT': {
                const { labelKey, amountKey, percentKey } = cfg;
                // 根据金额字段区分堂食 / 外卖，用于同步对应侧的 mode、trigger
                const side =
                    amountKey === 'dine_in_max_discount_amount' ? 'dine_in' : 'delivery';

                /** 单条圆角条容器样式（白底 + 设计稿投影） */
                const discountInputStripSx = {
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1,
                    borderRadius: '16px',
                    boxShadow: `0 0 4px 0 ${getColor(COLOR_KEY.shadow50)}`,
                    backgroundColor: getColor(COLOR_KEY.fillNeutral100),
                    minHeight: 48,
                } as const;

                /**
                 * 更新金额或百分比并立刻推导 mode / OR、互斥清空另一字段
                 * @param key - 金额或百分比字段名
                 * @param raw - 输入框原始字符串
                 */
                const patchDiscountValue = (
                    key: typeof amountKey | typeof percentKey,
                    raw: string,
                ) => {
                    let num: number | undefined;
                    if (raw === '') {
                        num = undefined;
                    } else {
                        const n = Number(raw);
                        if (Number.isNaN(n)) {
                            num = undefined;
                        } else {
                            num = Number(n.toFixed(2));
                        }
                    }
                    setFormValue((prev) => {
                        const merged = { ...prev, [key]: num } as RiskThresholdRequest;
                        return applyMaxDiscountSideSync(merged, side);
                    });
                };

                return (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1, pr: 2 }}>{renderLeftText(labelKey)}</Box>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                                width: 256,
                                minWidth: 256,
                                maxWidth: '100%',
                            }}
                        >
                            <Box sx={discountInputStripSx} className='risk-discount-input-strip'>
                                <Typography
                                    variant='body1'
                                    sx={{
                                        color: getColor(COLOR_KEY.textPrimary300),
                                        flexShrink: 0,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {i18n.t('intelligent_risk_control_page_form_discount_input_amount_hint')}
                                </Typography>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <CommonInput
                                        className='risk-form-item'
                                        inputType='INPUT'
                                        type='number'
                                        size='small'
                                        fullWidth
                                        value={formValue[amountKey] ?? ''}
                                        inputProps={{
                                            inputMode: 'decimal',
                                            pattern: '^\\d+(\\.\\d{0,2})?$',
                                        }}
                                        onChange={(e) => patchDiscountValue(amountKey, e.target.value)}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position='end'>€</InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>
                            </Box>
                            <Box sx={discountInputStripSx} className='risk-discount-input-strip'>
                                <Typography
                                    variant='body1'
                                    sx={{
                                        color: getColor(COLOR_KEY.textPrimary300),
                                        flexShrink: 0,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {i18n.t('intelligent_risk_control_page_form_discount_input_percent_hint')}
                                </Typography>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <CommonInput
                                        className='risk-form-item'
                                        inputType='INPUT'
                                        type='number'
                                        size='small'
                                        fullWidth
                                        value={formValue[percentKey] ?? ''}
                                        inputProps={{
                                            inputMode: 'decimal',
                                            pattern: '^\\d+(\\.\\d{0,2})?$',
                                        }}
                                        onChange={(e) => patchDiscountValue(percentKey, e.target.value)}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position='end'>%</InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                );
            }
            // 纯数字输入配置
            case 'NUMBER': {
                const { key, labelKey, adornment } = cfg;
                return renderSettingRow(
                    labelKey,
                    <Box sx={{ width: 160 }}>
                        <CommonInput
                            className='risk-form-item'
                            inputProps={{ min: 1, inputMode: 'numeric', pattern: '^\\d+$' }}
                            key={key}
                            required
                            inputType='INPUT'
                            type='number'
                            size='small'
                            value={(formValue[key] as number | '') ?? ''}
                            onChange={(e) => {
                                const raw = e.target.value;
                                if (raw === '') {
                                    handleChange(key, undefined as any);
                                    return;
                                }
                                // 数量/分钟：只保留整数部分
                                const num = Number(raw);
                                if (Number.isNaN(num)) {
                                    handleChange(key, undefined as any);
                                    return;
                                }
                                const intVal = Math.trunc(num);
                                handleChange(key, intVal as any);
                            }}
                            InputProps={adornment ? { endAdornment: adornment } : undefined}
                        />
                    </Box>,
                );
            }
            // 开关类配置（布尔值）；可带 subLabelKey，与设计稿「主标题 + 括号内次要说明」一致
            case 'SWITCH': {
                const { key, labelKey, subLabelKey } = cfg;
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                        <Box sx={{ flex: 1, pr: 2, minWidth: 0 }}>
                            {subLabelKey ? (
                                <Box component="span" sx={{ display: 'inline' }}>
                                    <Typography
                                        component="span"
                                        variant="h3"
                                        sx={{ color: getColor(COLOR_KEY.textDark) }}
                                    >
                                        {i18n.t(labelKey)}
                                    </Typography>
                                    <Typography
                                        component="span"
                                        variant="body1"
                                        sx={{
                                            fontWeight: 500,
                                            fontSize: 16,
                                            lineHeight: '24px',
                                        }}
                                    >
                                        {i18n.t(subLabelKey)}
                                    </Typography>
                                </Box>
                            ) : (
                                renderLeftText(labelKey)
                            )}
                        </Box>
                        <Box sx={{ flexShrink: 0 }}>
                            <AntSwitch
                                checked={formValue[key] as boolean}
                                onChange={(_, checked) =>
                                    handleChange(key, checked as RiskThresholdRequest[typeof key])
                                }
                            />
                        </Box>
                    </Box>
                );
            }
            // 邮件通知配置（开关 + 邮箱 + 发送模式）
            case 'EMAIL_NOTIFY': {
                return (
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ flex: 1 }}>
                                {renderLeftText(
                                    'intelligent_risk_control_page_form_risk_notify_email_enabled',
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AntSwitch
                                    checked={formValue.risk_notify_email_enabled}
                                    onChange={(_, checked) =>
                                        handleChange('risk_notify_email_enabled', checked)
                                    }
                                />
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
                            <Box sx={{ flex: 6 }}>
                                <CommonInput
                                    className='risk-form-email-item'
                                    regularType='email'
                                    required
                                    inputType='INPUT'
                                    size='small'
                                    value={formValue.risk_notify_email_receiver ?? ''}
                                    onChange={(e) =>
                                        handleChange('risk_notify_email_receiver', e.target.value)
                                    }
                                    fullWidth
                                />
                            </Box>
                            <Box sx={{ flex: 4 }}>
                                <CommonInput
                                    className='risk-form-item'
                                    required
                                    inputType='SELECT'
                                    size='small'
                                    value={formValue.risk_notify_email_send_mode}
                                    onChange={(e) =>
                                        handleChange(
                                            'risk_notify_email_send_mode',
                                            e.target
                                                .value as RiskThresholdRequest['risk_notify_email_send_mode'],
                                        )
                                    }
                                    items={[
                                        {
                                            value: 'DAILY',
                                            label: i18n.t(
                                                'intelligent_risk_control_page_form_risk_notify_email_send_mode_daily',
                                            ),
                                        },
                                        {
                                            value: 'REALTIME',
                                            label: i18n.t(
                                                'intelligent_risk_control_page_form_risk_notify_email_send_mode_realtime',
                                            ),
                                        },
                                    ]}
                                    fullWidth
                                />
                            </Box>
                        </Box>
                    </Box>
                );
            }
            // 开关+WhatsApp号码
            case 'WHATSAPP_NOTIFY': {
                return (
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ flex: 1 }}>
                                {renderLeftText(
                                    'intelligent_risk_control_page_form_risk_notify_whatsapp_enabled',
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AntSwitch
                                    checked={formValue.risk_notify_whatsapp_enabled}
                                    onChange={(_, checked) =>
                                        handleChange('risk_notify_whatsapp_enabled', checked)
                                    }
                                />
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
                            <Box sx={{ width: '100px' }}>
                                <CommonInput
                                    className='risk-form-email-item'
                                    required
                                    inputType='COUNTRY_SELECT'
                                    label={(<></>)}
                                    size='small'
                                    value={formValue.risk_notify_whatsapp_prefix ?? '39'}
                                    handleChangeCountry={(value) => {
                                        handleChange('risk_notify_whatsapp_prefix', value)
                                    }}
                                />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <CommonInput
                                    className='risk-form-email-item'
                                    regularType='phone'
                                    required
                                    inputType='INPUT'
                                    size='small'
                                    value={formValue.risk_notify_whatsapp_receiver ?? ''}
                                    onChange={(e) =>
                                        handleChange('risk_notify_whatsapp_receiver', e.target.value)
                                    }
                                />
                            </Box>
                        </Box>
                    </Box>
                )
            }
            // 堂食：订单数 + 间隔分钟；外卖：订单数 + 间隔秒（delivery_order_close_check_window_seconds）
            case 'ORDER_CLOSE_CHECK_WINDOW': {
                const { labelKey, numberKey, windowDurationKey, windowDurationUnit } = cfg;
                /**
                 * 解析正整数输入，空则写 undefined
                 * @param raw - 输入框字符串
                 */
                const parsePositiveInt = (raw: string): number | undefined => {
                    if (raw === '') {
                        return undefined;
                    }
                    const num = Number(raw);
                    if (Number.isNaN(num)) {
                        return undefined;
                    }
                    return Math.trunc(num);
                };
                return (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ flex: 1, pr: 2 }}>{renderLeftText(labelKey)}</Box>
                        <Box sx={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                            <Box sx={{ width: '160px' }}>
                                <CommonInput
                                    className='risk-form-item'
                                    required
                                    inputType='INPUT'
                                    size='small'
                                    value={formValue[numberKey] ?? ''}
                                    onChange={(e) => {
                                        const intVal = parsePositiveInt(e.target.value);
                                        handleChange(numberKey, intVal as RiskThresholdRequest[typeof numberKey]);
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position='end'>
                                                {i18n.t('intelligent_risk_control_page_column_order_count')}
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>
                            <Box sx={{ width: '160px' }}>
                                <CommonInput
                                    className='risk-form-item'
                                    required
                                    inputType='INPUT'
                                    type='number'
                                    size='small'
                                    value={formValue[windowDurationKey] ?? ''}
                                    inputProps={{ min: 1, inputMode: 'numeric', pattern: '^\\d+$' }}
                                    onChange={(e) => {
                                        const intVal = parsePositiveInt(e.target.value);
                                        const bounded =
                                            intVal === undefined
                                                ? undefined
                                                : Math.max(1, intVal);
                                        handleChange(
                                            windowDurationKey,
                                            bounded as RiskThresholdRequest[typeof windowDurationKey],
                                        );
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position='end'>
                                                {windowDurationUnit === 'minutes' ? 'min' : 's'}
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>
                        </Box>
                    </Box>
                );
            }
            default:
                return null;
        }
    };

    // 初始化：根据餐厅信息填充风控阈值表单
    const init = async () => {
        // 当前餐厅id
        const id = mainPageData.currentRestaurant._id;

        if (!id) return;

        setLoading(true);
        const result = await apiManage.get_restaurant_info(id);
        if (result && !(result as any).error) {
            const picked: Partial<RiskThresholdRequest> = _.pick(result as any, [
                'dine_in_max_discount_enabled',
                'dine_in_max_discount_mode',
                'dine_in_max_discount_amount',
                'dine_in_max_discount_percent',
                'dine_in_max_discount_trigger_logic',

                'dine_in_max_refund_item_count',
                'dine_in_max_refund_amount',
                'dine_in_abnormal_item_count_threshold',
                'dine_in_max_dining_duration_minutes',
                'dine_in_order_close_interval_minutes',
                'dine_in_order_close_check_window_number',

                'delivery_max_discount_enabled',
                'delivery_max_discount_mode',
                'delivery_max_discount_amount',
                'delivery_max_discount_percent',
                'delivery_max_discount_trigger_logic',
                'delivery_order_close_check_window_seconds',
                'delivery_order_close_check_window_number',

                'cancel_order_risk_alert_enabled',
                'risk_control_enabled',
                'shift_briefing_whatsapp_enabled',
                'risk_notify_whatsapp_enabled',
                'risk_notify_whatsapp_receiver',
                'risk_notify_whatsapp_prefix',
                'risk_notify_email_enabled',
                'risk_notify_email_receiver',
                'risk_notify_email_send_mode',
            ]);

            setFormValue(() => {
                const merged = migrateDineInOrderCloseWindowNumberDefault({
                    ...defaultFormValue,
                    ...picked,
                } as RiskThresholdRequest);
                return syncAllMaxDiscountBlocks(merged);
            });
        }
        setLoading(false);
    };


    useEffect(() => {
        // 初始化
        init();
    }, [mainPageData.currentRestaurant])

    // 设计稿：卡片投影 0 0 4px 主题 800-25
    const cardSurfaceStyle: React.CSSProperties = {
        borderRadius: 16,
        boxShadow: `0 0 4px 0 ${getColor(COLOR_KEY.shadow25)}`,
    };

    return (
        <CardView
            title={i18n.t('intelligent_risk_control_page_text_risk_control_threshold_settings')}
            titleTypographyProps={{
                variant: 'h3',
                sx: { color: getColor(COLOR_KEY.textDark) },
            }}
            className='mt16 risk-threshold-settings'
            actionRender={
                <Box display='flex' gap={3} alignItems='center' flexWrap='wrap'>
                    <Button
                        variant='contained'
                        disableElevation
                        onClick={handleApplyRecommend}
                        className='action-btn action-btn--primary'
                        sx={{
                            borderRadius: '16px',
                            px: 2,
                            py: 2,
                            minWidth: 152,
                            backgroundColor: getColor(COLOR_KEY.fillPrimary300),
                            border: `1px solid ${getColor(COLOR_KEY.strokePrimary300)}`,
                            '&:hover': {
                                backgroundColor: getColor(COLOR_KEY.fillPrimary300),
                                opacity: 0.92,
                            },
                        }}
                    >
                        <Typography variant='body1' sx={{ color: getColor(COLOR_KEY.textLight) }}>
                            {i18n.t('intelligent_risk_control_page_button_apply_recommend')}
                        </Typography>
                    </Button>
                    <LoadingOverlayButton
                        type='button'
                        variant='outlined'
                        loading={saveLoading}
                        loadingIndicatorSize={22}
                        onClick={() => void handleSave()}
                        className='action-btn'
                        sx={{
                            borderRadius: '16px',
                            px: 2,
                            py: 2,
                            borderColor: getColor(COLOR_KEY.strokePrimary300),
                            color: getColor(COLOR_KEY.textPrimary300),
                            minWidth: 152,
                        }}
                    >
                        <Typography variant='body1' sx={{ color: getColor(COLOR_KEY.textPrimary300) }}>
                            {i18n.t('general_text_save')}
                        </Typography>
                    </LoadingOverlayButton>
                </Box>
            }
            contentRender={(
                <Box sx={{ px: 2, py: 2 }}>
                    {loading ? (
                        // 骨架屏：左右两列各若干行，模拟表单布局（行距与设计稿 32px 接近）
                        <Grid container spacing={4} alignItems='flex-start'>
                            <Grid item xs={12} md={6} sx={{ borderRight: { md: `1px solid ${getColor(COLOR_KEY.strokeNeutral800A25)}` }, pr: { md: 2 } }}>
                                {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                                    <Box key={i} sx={{ my: 8 }}>
                                        <Skeleton variant='rectangular' width='100%' height={80} />
                                    </Box>
                                ))}
                            </Grid>
                            <Grid item xs={12} md={6} sx={{ pl: { md: 2 } }}>
                                {[1, 2, 3, 4, 5].map((i) => (
                                    <Box key={i} sx={{ my: 8 }}>
                                        <Skeleton variant='rectangular' width='100%' height={80} />
                                    </Box>
                                ))}
                            </Grid>
                        </Grid>
                    ) : (
                        <>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    pb: 4,
                                    mb: 4,
                                    borderBottom: `1px dashed ${getColor(COLOR_KEY.strokeNeutral800A25)}`,
                                }}
                            >
                                {renderLeftText('intelligent_risk_control_page_form_risk_control_enabled')}
                                <AntSwitch
                                    checked={Boolean(formValue.risk_control_enabled)}
                                    inputProps={{
                                        'aria-label': i18n.t(
                                            'intelligent_risk_control_page_form_risk_control_enabled',
                                        ),
                                    }}
                                    onChange={(_, checked) =>
                                        handleChange('risk_control_enabled', checked)
                                    }
                                />
                            </Box>
                            <Grid container spacing={0} alignItems='flex-start'>
                            <Grid
                                item
                                xs={12}
                                md={6}
                                sx={{
                                    pr: 4,
                                    borderRight: { md: `1px solid ${getColor(COLOR_KEY.strokeNeutral800A25)}` }
                                }}
                            >
                                {fieldConfigsLeft.map((cfg, index) => (
                                    <Box
                                        key={
                                            cfg.type === 'ORDER_CLOSE_CHECK_WINDOW'
                                                ? `${cfg.numberKey}-${cfg.windowDurationKey}`
                                                : (cfg as { key?: string }).key ?? index
                                        }
                                        sx={{
                                            // 虚线上下各 32px：首行无上间距；末行无下间距与分割线
                                            pt: index > 0 ? 8 : 0,
                                            pb: index < fieldConfigsLeft.length - 1 ? 8 : 0,
                                            borderBottom:
                                                index < fieldConfigsLeft.length - 1
                                                    ? `1px dashed ${getColor(COLOR_KEY.strokeNeutral800A25)}`
                                                    : 'none',
                                        }}
                                    >
                                        {renderFieldByConfig(cfg)}
                                    </Box>
                                ))}
                            </Grid>

                            <Grid item xs={12} md={6} sx={{ pl: 4 }}>
                                {fieldConfigsRight.map((cfg, index) => (
                                    <Box
                                        key={
                                            cfg.type === 'DISCOUNT'
                                                ? cfg.labelKey
                                                : cfg.type === 'EMAIL_NOTIFY'
                                                    ? cfg.labelKey
                                                    : cfg.type === 'ORDER_CLOSE_CHECK_WINDOW'
                                                        ? `${cfg.numberKey}-${cfg.windowDurationKey}`
                                                        : (cfg as { key?: string }).key ?? index
                                        }
                                        sx={{
                                            pt: index > 0 ? 4 : 0,
                                            pb: index < fieldConfigsRight.length - 1 ? 4 : 0,
                                            borderBottom:
                                                index < fieldConfigsRight.length - 1
                                                    ? `1px dashed ${getColor(COLOR_KEY.strokeNeutral800A25)}`
                                                    : 'none',
                                        }}
                                    >
                                        {renderFieldByConfig(cfg)}
                                    </Box>
                                ))}
                            </Grid>
                            </Grid>
                        </>
                    )}
                </Box>
            )}
            style={cardSurfaceStyle}
        />
    );
});

export default RiskThresholdSettings;

type FieldConfig =
    | {
        key: string;
        type: 'DISCOUNT';
        labelKey: string;
        modeKey: keyof Pick<
            RiskThresholdRequest,
            'dine_in_max_discount_mode' | 'delivery_max_discount_mode'
        >;
        amountKey: keyof Pick<
            RiskThresholdRequest,
            'dine_in_max_discount_amount' | 'delivery_max_discount_amount'
        >;
        percentKey: keyof Pick<
            RiskThresholdRequest,
            'dine_in_max_discount_percent' | 'delivery_max_discount_percent'
        >;
        triggerLogicKey: keyof Pick<
            RiskThresholdRequest,
            'dine_in_max_discount_trigger_logic' | 'delivery_max_discount_trigger_logic'
        >;
    }
    | {
        type: 'NUMBER';
        labelKey: string;
        key: keyof RiskThresholdRequest;
        adornment?: ReactNode;
    }
    | {
        type: 'SWITCH';
        labelKey: string;
        key: keyof RiskThresholdRequest;
        /** 设计稿中主标题后括号内说明（较小字、次要色），可选 */
        subLabelKey?: string;
    }
    | {
        type: 'EMAIL_NOTIFY';
        labelKey: string;
        key: string;
    }
    | {
        type: 'WHATSAPP_NOTIFY';
        labelKey: string;
        key: string;
    }
    | {
        type: 'ORDER_CLOSE_CHECK_WINDOW';
        labelKey: string;
        numberKey: keyof Pick<
            RiskThresholdRequest,
            | 'dine_in_order_close_check_window_number'
            | 'delivery_order_close_check_window_number'
        >;
        windowDurationKey: keyof Pick<
            RiskThresholdRequest,
            | 'dine_in_order_close_interval_minutes'
            | 'delivery_order_close_check_window_seconds'
        >;
        /** 第二格单位：堂食为分钟，外卖接口为秒 */
        windowDurationUnit: 'minutes' | 'seconds';
    };

